import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import {
  trpcIdentityGetOrganization,
  trpcIdentityGetIdentityUserContact,
} from '@trashlab/api/src/generated/hooks';
import { Loader } from '@mantine/core';

/**
 * AppSessionContext provides organization and auth user data to all authenticated
 * components. This replaces the pattern of calling useQuery hooks directly in
 * multiple places, ensuring data is fetched once and shared via context.
 *
 * Mount this provider in the authenticated layout (_app.tsx) so that:
 * 1. Public pages don't trigger auth queries
 * 2. All authenticated pages share the same organization/user data
 * 3. Data is guaranteed to be available to consumers (no undefined checks needed)
 */

// Type definitions derived from the tRPC query return types
export type OrganizationData = NonNullable<
  ReturnType<typeof trpcIdentityGetOrganization.useQuery>['data']
>;

export type AuthUserData = NonNullable<
  ReturnType<typeof trpcIdentityGetIdentityUserContact.useQuery>['data']
>;

type OrganizationQuery = ReturnType<typeof trpcIdentityGetOrganization.useQuery>;
type AuthUserQuery = ReturnType<typeof trpcIdentityGetIdentityUserContact.useQuery>;

export interface AppSessionContextValue {
  /**
   * Organization data. Guaranteed to be defined when inside the provider.
   */
  organization: OrganizationData;

  /**
   * Auth user data (identity user contact). Guaranteed to be defined when inside the provider.
   */
  authUser: AuthUserData;

  /**
   * Full query object for organization - useful for advanced scenarios
   * like refetching or accessing query state.
   */
  organizationQuery: OrganizationQuery;

  /**
   * Full query object for auth user - useful for advanced scenarios
   * like refetching or accessing query state.
   */
  authUserQuery: AuthUserQuery;
}

const AppSessionContext = createContext<AppSessionContextValue | null>(null);

export interface AppSessionProviderProps {
  children: ReactNode;
  /**
   * Optional loading fallback. Defaults to a centered loader.
   */
  loadingFallback?: ReactNode;
}

/**
 * Provides organization and auth user data to all child components.
 * Does not render children until both organization and auth user data are loaded.
 * This guarantees that consumers can access data without undefined checks.
 *
 * Throws errors if either query fails to avoid infinite loading spinners.
 */
export function AppSessionProvider({ children, loadingFallback }: AppSessionProviderProps) {
  const organizationQuery = trpcIdentityGetOrganization.useQuery();
  const authUserQuery = trpcIdentityGetIdentityUserContact.useQuery();

  // Throw errors to avoid infinite loading spinners
  // These will be caught by error boundaries
  if (organizationQuery.error) {
    throw organizationQuery.error;
  }
  if (authUserQuery.error) {
    throw authUserQuery.error;
  }

  // Don't render children until both queries have data
  // This guarantees data is available to all consumers
  if (!organizationQuery.data || !authUserQuery.data) {
    return loadingFallback ?? <Loader className="mx-auto mt-8" />;
  }

  const value: AppSessionContextValue = {
    organization: organizationQuery.data,
    authUser: authUserQuery.data,
    organizationQuery,
    authUserQuery,
  };

  return (
    <AppSessionContext.Provider value={value}>
      {children}
    </AppSessionContext.Provider>
  );
}

/**
 * Hook to access the app session context.
 * Throws if used outside of AppSessionProvider.
 * Data is guaranteed to be defined.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { organization, authUser } = useAppSession();
 *   // No need for undefined checks - data is guaranteed
 *   return <div>{organization.name} - {authUser.email}</div>;
 * }
 * ```
 */
export function useAppSession(): AppSessionContextValue {
  const context = useContext(AppSessionContext);
  if (!context) {
    throw new Error(
      'useAppSession must be used within an AppSessionProvider. ' +
      'Make sure the component is rendered inside the authenticated layout.',
    );
  }
  return context;
}

/**
 * Hook to access the app session context, returning null if outside provider.
 * Useful for components that may render in both authenticated and public contexts,
 * or for hooks that need to fall back to direct queries.
 */
export function useAppSessionSafe(): AppSessionContextValue | null {
  return useContext(AppSessionContext);
}
